import { Users, Timer, Youtube, User, LayoutGrid, HelpCircle, Layers, Bell } from "lucide-react";
import { Card } from "@/components/ui/card";

const features = [
  {
    icon: Users,
    title: "Group Mode",
    description: "Study together with peers from around the world. Join virtual rooms and stay motivated.",
    gradient: "from-primary/20 to-primary/5",
    iconColor: "text-primary",
    href: "/group-study/",
  },
  {
    icon: Timer,
    title: "Timer Mode",
    description: "Boost your focus with built-in Pomodoro timers. Work smart, not hard.",
    gradient: "from-secondary/20 to-secondary/5",
    iconColor: "text-secondary",
    href: "/study-timer/",
  },
  {
    icon: Youtube,
    title: "YT Mode",
    description: "Stream educational content while you work. Learn and grow continuously.",
    gradient: "from-accent/20 to-accent/5",
    iconColor: "text-accent",
    href: "/study-stream/",
  },
  {
    icon: User,
    title: "Solo Mode",
    description: "Focus in your personal workspace. Privacy meets productivity.",
    gradient: "from-muted-foreground/20 to-muted-foreground/5",
    iconColor: "text-foreground",
    href: "/online-workspace/",
  },
  {
    icon: LayoutGrid,
    title: "Task",
    description: "Organize your work with a Kanban board. Drag, drop, and conquer your to-do list.",
    gradient: "from-primary/20 to-primary/5",
    iconColor: "text-primary",
    href: "/task-management/",
  },
  {
    icon: HelpCircle,
    title: "Quizzes",
    description: "Test your knowledge with interactive quizzes. Learn smarter, not harder.",
    gradient: "from-secondary/20 to-secondary/5",
    iconColor: "text-secondary",
    href: "/quizzes/",
  },
  {
    icon: Layers,
    title: "Flash Cards",
    description: "Master any subject with digital flash cards. Perfect for memorization and review.",
    gradient: "from-accent/20 to-accent/5",
    iconColor: "text-accent",
    href: "/flash-cards/",
  },
  {
    icon: Bell,
    title: "Reminders",
    description: "Never miss a deadline. Set smart reminders to keep your studies on track.",
    gradient: "from-muted-foreground/20 to-muted-foreground/5",
    iconColor: "text-foreground",
    href: "/reminders/",
  },
];

export const Features = () => {
  return (
    <section className="py-24 relative overflow-hidden" aria-labelledby="features-title">
      <div className="absolute inset-0 bg-gradient-to-b from-muted via-background to-background" aria-hidden="true" />
      
      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-16 space-y-4 animate-fade-in">
          <h2 id="features-title" className="font-display text-4xl sm:text-5xl font-bold">
            How To Be <span className="text-gradient">Productive</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Choose your perfect mode and unlock your full potential
          </p>
        </header>
        
        <nav className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" aria-label="Productivity features">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <a key={feature.title} href={feature.href} className="block">
                <article
                  className="group relative overflow-hidden border-border bg-card hover:bg-card/80 p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl animate-fade-in h-full rounded-lg border"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Gradient Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} aria-hidden="true" />
                  
                  {/* Content */}
                  <div className="relative z-10 space-y-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300`} aria-hidden="true">
                      <Icon className={`h-7 w-7 ${feature.iconColor}`} />
                    </div>
                    
                    <h3 className="text-xl font-bold text-foreground">
                      {feature.title}
                    </h3>
                    
                    <p className="text-muted-foreground leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                  
                  {/* Hover Glow Effect */}
                  <div className="absolute -bottom-2 -right-2 w-24 h-24 bg-primary/10 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true" />
                </article>
              </a>
            );
          })}
        </nav>
      </div>
    </section>
  );
};
